import type { AliasGroupPillState } from './alias-group-pill';

export interface AliasPillInput {
  id: number;
  // Stable cluster id (the lowest entity id in the cluster); undefined when the
  // entity has no aliases.
  aliasGroupId?: string;
}

// Ported from mock-ui ReviewMatch.computeAliasPillStates, adapted to take a
// clusterSizeByGroup map (the real alias cluster's total member count) instead
// of the mock's global ALIAS_CLUSTERS registry.
//
// For each visible entity in an alias cluster, decide whether the cluster is
// fully visible (in-list-only), partly visible (mixed), or only-elsewhere
// (out-of-list-only). Group numbers are assigned in first-seen order, for
// on-screen reference only.
export function computeAliasPillStates(
  visible: AliasPillInput[],
  clusterSizeByGroup: Record<string, number>
): Map<number, AliasGroupPillState> {
  const result = new Map<number, AliasGroupPillState>();

  const groupOrder: string[] = [];
  for (const member of visible) {
    if (member.aliasGroupId && !groupOrder.includes(member.aliasGroupId)) {
      groupOrder.push(member.aliasGroupId);
    }
  }

  for (const member of visible) {
    const groupId = member.aliasGroupId;
    if (!groupId) continue;

    const inListSize = visible.filter((v) => v.aliasGroupId === groupId).length;
    const inListPeers = inListSize - 1;
    const clusterTotal = clusterSizeByGroup[groupId] ?? inListSize;
    const outOfListCount = Math.max(0, clusterTotal - Math.max(1, inListSize));
    const groupNumber = groupOrder.indexOf(groupId) + 1;

    if (inListPeers > 0 && outOfListCount > 0) {
      result.set(member.id, { kind: 'mixed', groupNumber });
    } else if (inListPeers > 0) {
      result.set(member.id, { kind: 'in-list-only', groupNumber });
    } else if (outOfListCount > 0) {
      result.set(member.id, { kind: 'out-of-list-only' });
    }
  }

  return result;
}
